import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, BookOpen, Scale, FileText, ChevronRight, AlertTriangle, X } from 'lucide-react';
import api from '../lib/api';

const CATEGORY_LABELS = {
  crime: { fr: 'Crime', color: 'bg-red-100 text-red-800 border-red-200' },
  delit: { fr: 'Delit', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  contravention: { fr: 'Contravention', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
};

const SOURCE_LABELS = {
  code_penal: 'Code Penal',
  code_penal_militaire: 'Code Penal Militaire',
  code_famille: 'Code de la Famille',
  code_minier: 'Code Minier',
  code_procedure_penale: 'Code de Procedure Penale',
  constitution: 'Constitution RDC',
  code_civil: 'Code Civil',
  droit_commercial: 'Droit Commercial',
  protection_enfant: 'Protection de l\'Enfant',
  loi_speciale: 'Lois Speciales',
  code_route: 'Code de la Route',
};

const LegalReferencePopup = ({ open, onClose, onSelect, mode = 'browse' }) => {
  const [offenses, setOffenses] = useState([]);
  const [legalEntries, setLegalEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('infractions');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;

      const [offResp, libResp] = await Promise.all([
        api.get('/offenses', { params }),
        api.get('/legal-library', { params: { search } }),
      ]);
      setOffenses(offResp.data || []);
      setLegalEntries(libResp.data || []);
    } catch (err) {
      console.error('Failed to load legal data:', err);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => loadData(), 300);
    return () => clearTimeout(timer);
  }, [search, categoryFilter, open, loadData]);

  const handleSelectOffense = (offense) => {
    if (mode === 'select' && onSelect) {
      onSelect(offense);
      onClose();
    } else {
      setSelectedEntry(offense);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0" data-testid="legal-reference-popup">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Scale className="w-5 h-5 text-primary" />
            {mode === 'select' ? 'Selectionner une infraction' : 'Bibliotheque juridique RDC'}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-3 border-b shrink-0 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une infraction, un article, une peine..."
              className="pl-10"
              data-testid="legal-search-input"
            />
            {search && (
              <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setSearch('')}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>

          {/* Category filters */}
          <div className="flex gap-1.5 flex-wrap">
            <Button variant={!categoryFilter ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setCategoryFilter('')}>
              Tous
            </Button>
            <Button variant={categoryFilter === 'crime' ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setCategoryFilter('crime')}>
              Crimes ({offenses.filter(o => o.category === 'crime').length})
            </Button>
            <Button variant={categoryFilter === 'delit' ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setCategoryFilter('delit')}>
              Delits ({offenses.filter(o => o.category === 'delit').length})
            </Button>
            <Button variant={categoryFilter === 'contravention' ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setCategoryFilter('contravention')}>
              Contraventions ({offenses.filter(o => o.category === 'contravention').length})
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-6 mt-2 shrink-0">
            <TabsTrigger value="infractions" className="gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Infractions ({offenses.length})
            </TabsTrigger>
            <TabsTrigger value="library" className="gap-1.5">
              <BookOpen className="w-3.5 h-3.5" />
              Textes de loi ({legalEntries.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="infractions" className="flex-1 min-h-0 px-6 pb-4">
            <div className="flex gap-4 h-full">
              <ScrollArea className={selectedEntry ? 'w-1/2' : 'w-full'}>
                <div className="space-y-1 pr-3">
                  {loading ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">Chargement...</div>
                  ) : offenses.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">Aucune infraction trouvee</div>
                  ) : (
                    offenses.map((o, i) => (
                      <div
                        key={o.offense_id || i}
                        className={`p-2.5 rounded-lg cursor-pointer border transition-colors ${
                          selectedEntry?.offense_id === o.offense_id ? 'bg-primary/10 border-primary/40' : 'hover:bg-muted/60 border-transparent'
                        }`}
                        onClick={() => handleSelectOffense(o)}
                        data-testid={`offense-item-${i}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium leading-tight">{o.name}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{o.reference}</p>
                          </div>
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${CATEGORY_LABELS[o.category]?.color || ''}`}>
                            {o.category}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>

              {selectedEntry && activeTab === 'infractions' && (
                <div className="w-1/2 border-l pl-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-base">{selectedEntry.name}</h3>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedEntry(null)}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="space-y-2.5">
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Categorie</p>
                      <Badge variant="outline" className={`mt-1 ${CATEGORY_LABELS[selectedEntry.category]?.color || ''}`}>
                        {selectedEntry.category}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Reference legale</p>
                      <p className="text-sm mt-0.5">{selectedEntry.reference}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Peine applicable</p>
                      <p className="text-sm mt-0.5 font-medium text-red-700 dark:text-red-400">{selectedEntry.penalty}</p>
                    </div>
                    {selectedEntry.source && (
                      <div>
                        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Source</p>
                        <p className="text-sm mt-0.5">{SOURCE_LABELS[selectedEntry.source] || selectedEntry.source}</p>
                      </div>
                    )}
                  </div>
                  {mode === 'select' && (
                    <Button className="w-full mt-4" onClick={() => { onSelect(selectedEntry); onClose(); }} data-testid="select-offense-btn">
                      <ChevronRight className="w-4 h-4 mr-2" />
                      Selectionner cette infraction
                    </Button>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="library" className="flex-1 min-h-0 px-6 pb-4">
            <ScrollArea className="h-full">
              <div className="space-y-2 pr-3">
                {legalEntries.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">Aucune entree trouvee</div>
                ) : (
                  legalEntries.map((e, i) => (
                    <div key={e.entry_id || i} className="p-3 rounded-lg border hover:bg-muted/40 transition-colors" data-testid={`legal-entry-${i}`}>
                      <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold">{e.title}</p>
                            <Badge variant="secondary" className="text-[10px]">{e.articles}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.description}</p>
                          {e.penalties && (
                            <p className="text-xs font-medium text-red-600 dark:text-red-400 mt-1">Peine: {e.penalties}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default LegalReferencePopup;
